import React, { useState } from 'react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { fetchWithCsrf } from '../../utils/csrf';

interface TenantCreationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
}

interface TenantCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: any) => void;
}

const TenantCreationWizard: React.FC<TenantCreationWizardProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [data, setData] = useState<TenantCreationData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'admin'
  });

  const updateData = (updates: Partial<TenantCreationData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Prepare the data for the API
      const requestData = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        role: data.role
      };

      // Make the API call to create the tenant
      const response = await fetchWithCsrf('/api/admin/tenants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle validation errors with specific field messages
        if (result.errors && typeof result.errors === 'object') {
          setValidationErrors(result.errors);
          const errorMessages = [];
          for (const [field, messages] of Object.entries(result.errors)) {
            if (Array.isArray(messages)) {
              errorMessages.push(`${field}: ${messages.join(', ')}`);
            }
          }
          toast.error(`Validation failed: ${errorMessages.join(', ')}`);
          return;
        }
        throw new Error(result.message || 'Failed to create tenant');
      }

      // Success - clear any previous errors and proceed
      setValidationErrors({});
      onComplete(result);
      toast.success('Tenant and user created successfully!');
      onClose();

      // Reset form
      setData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'admin'
      });
    } catch (error) {
      console.error('Tenant creation error:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to create tenant';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get validation error for a field
  const getFieldError = (fieldName: string): string[] => {
    return validationErrors[fieldName] || [];
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldName: string): boolean => {
    return getFieldError(fieldName).length > 0;
  };

  // Helper function to get input CSS classes with error styling
  const getInputClasses = (fieldName: string): string => {
    const baseClasses = "w-full rounded-lg border-2 transition-all duration-200 focus:ring-4 focus:ring-opacity-20 focus:outline-none";
    const normalClasses = "border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-gray-400";
    const errorClasses = "border-red-400 focus:border-red-500 focus:ring-red-500 bg-red-50";

    return `${baseClasses} ${hasFieldError(fieldName) ? errorClasses : normalClasses}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  Create New Tenant Account
                </h3>
                <p className="mt-1 text-sm text-blue-100">
                  Set up a new tenant with professional plan access
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-blue-100 transition-colors rounded-lg p-2 hover:bg-white/10"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-8 py-8 space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={data.firstName}
                    onChange={(e) => updateData({ firstName: e.target.value })}
                    className={`${getInputClasses('first_name')} px-4 py-3 text-sm`}
                    placeholder="John"
                    required
                  />
                  {hasFieldError('first_name') && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-500 mr-2"></span>
                      {getFieldError('first_name').join(', ')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={data.lastName}
                    onChange={(e) => updateData({ lastName: e.target.value })}
                    className={`${getInputClasses('last_name')} px-4 py-3 text-sm`}
                    placeholder="Doe"
                    required
                  />
                  {hasFieldError('last_name') && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-500 mr-2"></span>
                      {getFieldError('last_name').join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={data.email}
                  onChange={(e) => updateData({ email: e.target.value })}
                  className={`${getInputClasses('email')} px-4 py-3 text-sm`}
                  placeholder="john.doe@example.com"
                  required
                />
                {hasFieldError('email') && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="inline-block w-1 h-1 rounded-full bg-red-500 mr-2"></span>
                    {getFieldError('email').join(', ')}
                  </p>
                )}
              </div>

              {/* Password & Role Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={data.password}
                    onChange={(e) => updateData({ password: e.target.value })}
                    className={`${getInputClasses('password')} px-4 py-3 text-sm`}
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                  />
                  {hasFieldError('password') && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-500 mr-2"></span>
                      {getFieldError('password').join(', ')}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500 flex items-start">
                    <svg className="w-3 h-3 mr-1 mt-0.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    User can change on first login
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={data.role}
                    onChange={(e) => updateData({ role: e.target.value })}
                    className={`${getInputClasses('role')} px-4 py-3 text-sm`}
                    required
                  >
                    <option value="admin">Admin - Full Access</option>
                    <option value="manager">Manager - Standard Access</option>
                    <option value="user">User - Basic Access</option>
                  </select>
                  {hasFieldError('role') && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-500 mr-2"></span>
                      {getFieldError('role').join(', ')}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500 flex items-start">
                    <svg className="w-3 h-3 mr-1 mt-0.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    Admin includes billing access
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-5">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      Automatic Setup
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      A tenant account will be created with <strong>Professional Plan</strong> and
                      <strong> Active</strong> status. The subdomain will be auto-generated
                      (e.g., john-doe.optimancer.com).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-6 flex items-center justify-between border-t border-gray-200">
              <p className="text-xs text-gray-500">
                All fields marked with <span className="text-red-500">*</span> are required
              </p>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center px-6 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center px-6 py-2.5 border border-transparent shadow-sm text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-5 w-5 mr-2" />
                      Create Tenant Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TenantCreationWizard;
